// Percorso file: src/utils/roleUtils.js
//
// Utilità di lettura dei ruoli/contesti lato pagina.
//
// ⚠️ REGOLA DEL GATE UNICO: il calcolo di «quali ruoli possiede questa
// persona» vive in UN SOLO posto, src/auth/access.js (getActiveRoles /
// canAccess). Qui NON si riscrive quella logica: `hasRole` è solo la
// scorciatoia variadica che le pagine usano ("è admin?" = un ruolo fra
// due). Così non nascono un secondo gate e una seconda normalizzazione
// (audit 08 §0.6: ne esistevano 11 diversi).

import {
  canAccess,
  canonicalRole,
  currentRole,
  isActingAs,
  normalizeRole,
} from "../auth/access";

/**
 * Normalizzazione unica dei valori che arrivano dal documento utente:
 * String().trim().toUpperCase(). Ri-esportata da access.js perché le
 * pagine confrontino SEMPRE con lo stesso metro, sui due lati del
 * confronto (i dati storici hanno grafie miste: "Coach", "coach"…).
 */
export const normalizza = normalizeRole;

/**
 * RI-ESPORTAZIONI dalla sede unica — nessuna logica nuova qui dentro.
 *
 * Le pagine importano da `utils/roleUtils` da sempre; senza queste tre
 * righe chi ha bisogno del vocabolario o del cappello dovrebbe importare
 * da due sedi diverse, e la prima volta che qualcuno sbaglia strada
 * nasce la copia locale — cioè il difetto che il gate unico ha chiuso
 * (audit 08 §0.6: 11 gate diversi). Chi modifica il comportamento lo fa
 * in `auth/access.js`, e basta.
 *
 * - `canonicalRole` — grafia + vocabolario (Manager/TD/ManagerTD = COACH,
 *   Child = PLAYER). SOLA LETTURA: mai a monte di un salvataggio.
 * - `currentRole` / `isActingAs` — il cappello indossato ADESSO, per le
 *   VISTE PERSONALI e i filtri. **Mai per i comandi**: quelli si danno
 *   per PERMESSO, con `hasRole` qui sotto (decisione 1 dell'8/8/2026).
 */
export { canonicalRole, currentRole, isActingAs };

/**
 * Vero se la persona possiede ALMENO UNO dei ruoli richiesti.
 *
 * Un ruolo è «posseduto» se coincide con `user.role` di primo livello
 * OPPURE con il `role` di un context ACTIVE (definizione del gate unico,
 * diario 5/8/2026). Confronto su valori normalizzati da entrambe le parti.
 *
 * Esempio: `hasRole(user, "ADMIN", "SUPERADMIN")` → l'utente è un admin.
 *
 * @param {object|null} user utente di AuthContext
 * @param {...string} ruoli ruoli ammessi
 * @returns {boolean}
 */
export function hasRole(user, ...ruoli) {
  return ruoli.some((ruolo) => canAccess(user, ruolo));
}

/**
 * RI-ESPORTAZIONE DELLA SENTINELLA — nessuna logica qui dentro.
 *
 * `TUTTE_LE_SQUADRE` e `valeSuTutteLeSquadre` VIVEVANO qui (P7, dopo essere
 * salite da `pages/TeamAssignment.jsx`). Il cantiere «lettore unico» le ha
 * spostate in `utils/squadre.js`, accanto all'unica regola che le legge — il
 * ripiego di `squadreDellaTessera` — perché `approvedTeams` non è un fatto di
 * RUOLI: è un campo di TESSERA, e ciò che ne resta vivo è la lettura di
 * squadra. Tenerle qui avrebbe fatto di questo file, che si dichiara la
 * facciata del gate unico, un contenitore misto — cioè il modo in cui sono
 * nati gli undici gate dell'audit 08 §0.6.
 *
 * La riga qui sotto esiste perché gli importatori storici non cambino strada:
 * chi le importa da `utils/roleUtils` continua a trovarle. Senza, la prima
 * volta che qualcuno sbaglia sede nasce la copia locale — il difetto che
 * questo file esiste per impedire. Il corpo, e il lungo verbale su chi
 * scrive la sentinella, stanno in `utils/squadre.js`.
 */
export { TUTTE_LE_SQUADRE, valeSuTutteLeSquadre } from "./squadre";
